import { isNative, isWeb, match, type ReservationListFragment } from "@stepapp/core";
import { ReservationStatus } from "@stepapp/core/apiTypes";
import {
  Block,
  Button,
  Divider,
  palette,
  ServiceCardPlaceholder,
  SvgIcon,
  spacing,
  Touchable,
  Typography,
} from "@stepapp/ui";
import { Href, useRouter } from "expo-router";
import { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { Platform, SectionList, StyleSheet } from "react-native";
import { useRefreshControl } from "../../hooks";
import { AppStorage } from "../../services/AppStorage";
import { useDisplayIntercom } from "../../services/Intercom";
import { smallPageWrapper } from "../../styles/smallPageWrapper";
import PromoBanner from "../PromoBanner";
import UnregisteredPlaceholder from "../UnregisteredPlaceholder";
import { ServiceCard } from "./ServiceCard";

const ReservationCompletedStatuses = [
  ReservationStatus.Accepted,
  ReservationStatus.AwaitingPayment,
  ReservationStatus.Completed,
  ReservationStatus.PaymentReceived,
  ReservationStatus.Refunded,
  ReservationStatus.CustomerReview,
];

type Props = {
  orders: { title: string; data: ReservationListFragment[] }[];
  isCalendarScreen?: boolean;
  setUpdateServiceId?: (orderId: string) => void;
  setSelectPaymentMethod?: (paymentMethodId: string) => void;
  refetchReservations: () => Promise<NotWorth>;
  loading?: boolean;
};

export const ServiceList = ({
  orders,
  isCalendarScreen = false,
  setUpdateServiceId,
  setSelectPaymentMethod,
  refetchReservations,
  loading,
}: Props) => {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const countryCode = AppStorage().get("countryCode");
  const isAuthenticated = !!AppStorage().get("token");
  const displayIntercom = useDisplayIntercom(countryCode);
  const [refreshControl] = useRefreshControl(refetchReservations);

  const handlePressServiceCard = useCallback(
    (orderId: string, status: ReservationStatus, paymentMethodId: string) => () => {
      if (status === ReservationStatus.PaymentFailed) {
        if (isNative) {
          return navigate(`/calendar/retry-payment?reservationId=${orderId}`);
        }
        setSelectPaymentMethod?.(paymentMethodId);
        return setUpdateServiceId?.(orderId);
      }
      if (status === ReservationStatus.CustomerReview) {
        return navigate(`/calendar/${orderId}/rating/confirmation`);
      }
      return navigate(`/calendar/${orderId}/rating`);
    },
    [navigate, setUpdateServiceId, setSelectPaymentMethod],
  );

  const hasOrders = useMemo(() => orders.length > 0, [orders]);

  const handleNavigate = useCallback(
    (url: Href) => {
      navigate(url);
    },
    [navigate],
  );

  const renderItem = useCallback(
    ({ item, index }: { item: ReservationListFragment; index: number }) => {
      const paymentFailed = item.status === ReservationStatus.PaymentFailed && isCalendarScreen;
      const canLeaveReview =
        ReservationCompletedStatuses.includes(item?.status) &&
        !item?.review?.id &&
        isCalendarScreen;

      const isFirstItem = index === 0;

      return (
        <Block
          style={[
            isFirstItem && styles.additionalPadding,
            paymentFailed && styles.retryPaymentBackground,
            canLeaveReview && styles.reviewBackground,
          ]}
        >
          <Block style={styles.ordersList}>
            {paymentFailed && (
              <>
                <SvgIcon
                  color={palette.secondary.wrongPink}
                  name="alert-filled"
                  size={24}
                  style={styles.alertIcon}
                />
                <Typography variant="newH3">{t("screens.calendar.paymentFailed")}</Typography>
                <Typography type="secondary" variant="lightLabel">
                  {t("screens.calendar.additionalCardVerification")}
                </Typography>
              </>
            )}

            {canLeaveReview && (
              <>
                <Typography variant="newH3">
                  {t("screens.serviceRating.cleanLeaveRating")}
                </Typography>
                <Typography type="secondary" variant="lightLabel">
                  {t("screens.serviceRating.leaveOpinionAndRating")}
                </Typography>
              </>
            )}

            <ServiceCard
              isCalendarScreen={isCalendarScreen}
              canLeaveReview={!item?.review?.id}
              handlePress={handlePressServiceCard(
                item.id,
                item.status,
                item?.payment?.paymentMethodId || "",
              )}
              isSubscription={!!item?.subscription?.id}
              key={item.id}
              orderId={item.id}
              postalOrZipCode={item.address.postalOrZipCode}
              reservationName={item.bundle.name}
              services={item?.services}
              startDt={item?.startDate}
              timeConsumption={item?.timeConsumption}
              subscriptionStartDate={item?.subscription?.startDate}
              status={item.status}
              workers={item?.workers}
              customerNote={item?.customerNote}
            />
          </Block>
        </Block>
      );
    },
    [handlePressServiceCard, isCalendarScreen, t],
  );

  const ListHeaderComponent = useCallback(
    () => (
      <>
        {match(isCalendarScreen)
          .with(true, () => (
            <>
              <Block
                justifySpace
                row
                marginTop={isWeb ? 4 : 2}
                marginHorizontal={isWeb ? 0 : 2.5}
                marginBottom={isWeb ? 0 : 1}
              >
                <Typography variant="newH2">{t("screens.calendar.title")}</Typography>
                {isAuthenticated && (
                  <Button
                    label={t("screens.bookedServices.showCompleted")}
                    onPress={() => handleNavigate("/calendar/completed")}
                    style={styles.showCompletedButton}
                    type="link"
                  />
                )}
              </Block>
              {isWeb && <PromoBanner />}
            </>
          ))
          .otherwise(() => (
            <Block marginBottom={2} marginTop={isWeb ? 4 : 2} marginHorizontal={isWeb ? 0 : 2.5}>
              <Typography variant="newH2">{t("screens.completedServices.title")}</Typography>
            </Block>
          ))}
      </>
    ),
    [isCalendarScreen, isAuthenticated, handleNavigate, t],
  );

  const ListEmptyComponent = useCallback(() => {
    if (loading) {
      return (
        <Block style={styles.ordersList}>
          <ServiceCardPlaceholder marginTop={3} />
          <ServiceCardPlaceholder marginTop={3} />
        </Block>
      );
    }

    if (!isAuthenticated) {
      return (
        <Block marginHorizontal={isWeb ? 0 : 2.5}>
          <UnregisteredPlaceholder description={t("screens.calendar.notRegistered")} />
        </Block>
      );
    }

    return (
      <Block paddingTop={2} marginHorizontal={isWeb ? 5 : 2.5}>
        {isCalendarScreen ? (
          <Typography type="secondary" variant="body">
            {t("screens.bookedServices.emptyBookings")}
          </Typography>
        ) : (
          <>
            <Typography type="primary" variant="body">
              {t("screens.completedServices.completed")}
            </Typography>
            <Block marginTop={2.5}>
              <Typography type="secondary" variant="body">
                {t("screens.completedServices.emptyCompleted")}
              </Typography>
            </Block>
          </>
        )}
        <Block marginTop={2.5}>
          <Button
            label={t("screens.bookedServices.order")}
            onPress={() => handleNavigate("/")}
            type="primary"
          />
        </Block>
      </Block>
    );
  }, [loading, isCalendarScreen, isAuthenticated, handleNavigate, t]);

  const ListFooterComponent = useCallback(
    () => (
      <>
        {hasOrders && isCalendarScreen && (
          <Touchable onPress={() => displayIntercom()}>
            <Block marginHorizontal={isWeb ? 5 : 2.5} paddingTop={1} marginBottom={20}>
              <Typography center type="secondary" variant="lightLabel">
                {`${t("screens.calendar.serviceScheduleInfo")} `}
                <Typography decoration="underline" type="accent" variant="lightLabel">
                  {t("screens.calendar.writeToUs")}
                </Typography>
              </Typography>
            </Block>
          </Touchable>
        )}
      </>
    ),
    [isCalendarScreen, displayIntercom, hasOrders, t],
  );

  const renderSectionHeader = useCallback(
    ({ section }: ToDo) => {
      if (section.title === "" || section.data.length <= 0) {
        return null;
      }

      const paymentFailed =
        section.data[0]?.status === ReservationStatus.PaymentFailed && isCalendarScreen;
      const canLeaveReview =
        ReservationCompletedStatuses.includes(section.data[0]?.status) &&
        !section.data[0]?.review?.id &&
        isCalendarScreen;

      return (
        <Block
          alignCenter
          row
          flex={1}
          paddingTop={1}
          style={[
            paymentFailed && styles.retryPaymentBackground,
            canLeaveReview && styles.reviewBackground,
          ]}
        >
          <Divider color={palette.secondary.grey} style={styles.serviceDivider} />
          <Block marginHorizontal={3}>
            <Typography decoration="capitalize">{section.title}</Typography>
          </Block>
          <Divider color={palette.secondary.grey} style={styles.serviceDivider} />
        </Block>
      );
    },
    [isCalendarScreen],
  );

  return (
    <SectionList
      keyExtractor={(order: ReservationListFragment) => order.id}
      refreshControl={refreshControl()}
      renderItem={renderItem}
      ListEmptyComponent={ListEmptyComponent}
      ListHeaderComponent={ListHeaderComponent}
      ListFooterComponent={ListFooterComponent}
      style={isWeb && smallPageWrapper}
      renderSectionHeader={renderSectionHeader}
      sections={orders.map((section, index) => ({ ...section, index }))}
      showsVerticalScrollIndicator={false}
      stickySectionHeadersEnabled={false}
    />
  );
};

const styles = StyleSheet.create({
  additionalPadding: {
    paddingTop: spacing.unit,
  },
  alertIcon: {
    marginBottom: spacing.unit * 0.5,
  },
  serviceDivider: {
    flex: 1,
  },
  ordersList: {
    margin: isWeb ? "auto" : 0,
    maxWidth: spacing.unit * 82.5,
    paddingHorizontal: spacing.unit * (isWeb ? 0 : 2.5),
    ...Platform.select({
      default: {
        width: spacing.deviceWidth,
      },
      web: {
        width: "100%",
      },
    }),
  },
  retryPaymentBackground: {
    paddingBottom: spacing.unit * 3,
    backgroundColor: palette.common.wrongPink,
  },
  reviewBackground: {
    paddingBottom: spacing.unit * 3,
    backgroundColor: palette.common.lightPaleViolet,
  },
  showCompletedButton: {
    alignItems: "flex-end",
    flex: 1,
  },
});
